import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, List, ListItem, ListItemIcon, ListItemText, IconButton, Divider, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { logout, selectUser, selectUserProfile } from '../Commonlibs/userSlice';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FavoriteIcon from '@mui/icons-material/Favorite';
import MailIcon from '@mui/icons-material/Mail';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';

const Sidebar = ({
  onItemClick,
  activeItem: propActiveItem
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // Get user data from Redux store
  const user = useSelector(selectUser);
  const userProfile = useSelector(selectUserProfile);

  // State for profile data
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Individual active states for each menu item
  const [isDashboardActive, setIsDashboardActive] = useState(false);
  const [isEditProfileActive, setIsEditProfileActive] = useState(false);
  const [isMyPropertiesActive, setIsMyPropertiesActive] = useState(false);
  const [isInboxActive, setIsInboxActive] = useState(false);
  const [isTransactionsActive, setIsTransactionsActive] = useState(false);
  const [isMyActivityActive, setIsMyActivityActive] = useState(false);
  const [isWishlistActive, setIsWishlistActive] = useState(false);
  const [isRecentSearchesActive, setIsRecentSearchesActive] = useState(false);

  // Function to reset all active states
  const resetAllActiveStates = () => {
    setIsDashboardActive(false);
    setIsEditProfileActive(false);
    setIsMyPropertiesActive(false);
    setIsInboxActive(false);
    setIsTransactionsActive(false);
    setIsMyActivityActive(false);
    setIsWishlistActive(false);
    setIsRecentSearchesActive(false);
  };

  // Function to set active state based on path
  const setActiveStateFromPath = (path) => {
    resetAllActiveStates();
    if (path === '/' || path === '/dashboard' || path === '/sell-properties') {
      setIsDashboardActive(true);
    } else if (path.startsWith('/profile/edit')) {
      setIsEditProfileActive(true);
    } else if (path.startsWith('/sell-properties/seeAll') || path.startsWith('/sell-properties/edit')) {
      setIsMyPropertiesActive(true);
    } else if (path.startsWith('/sell-properties/inbox')) {
      setIsInboxActive(true);
    } else if (path.startsWith('/sell-properties/transactions')) {
      setIsTransactionsActive(true);
    } else if (path === '/profile') {
      setIsMyActivityActive(true);
    } else if (path.startsWith('/profile/wishlist')) {
      setIsWishlistActive(true);
    } else if (path.startsWith('/profile/recent-searches')) {
      setIsRecentSearchesActive(true);
    }
  };

  // Set initial active state based on current path
  useEffect(() => {
    setActiveStateFromPath(location.pathname);
  }, [location.pathname]);

  // Update active state when propActiveItem changes
  useEffect(() => {
    if (propActiveItem) {
      resetAllActiveStates();
      switch(propActiveItem) {
        case 'Dashboard':
          setIsDashboardActive(true);
          break;
        case 'Edit Profile':
          setIsEditProfileActive(true);
          break;
        case 'My Properties':
          setIsMyPropertiesActive(true);
          break;
        case 'Inbox':
          setIsInboxActive(true);
          break;
        case 'Transactions':
          setIsTransactionsActive(true);
          break;
        case 'My Activity':
          setIsMyActivityActive(true);
          break;
        case 'Wishlist':
          setIsWishlistActive(true);
          break;
        case 'Recent Searches':
          setIsRecentSearchesActive(true);
          break;
        default:
          break;
      }
    }
  }, [propActiveItem]);

  // Handle Dashboard click
  const handleDashboardClick = () => {
    resetAllActiveStates();
    setIsDashboardActive(true);
    navigate('/sell-properties');
    if (onItemClick) {
      onItemClick('Dashboard');
    }
  };

  // Handle Edit Profile click
  const handleEditProfileClick = () => {
    resetAllActiveStates();
    setIsEditProfileActive(true);
    navigate('/profile/edit');
    if (onItemClick) {
      onItemClick('Edit Profile');
    }
  };

  // Handle My Properties click
  const handleMyPropertiesClick = () => {
    resetAllActiveStates();
    setIsMyPropertiesActive(true);
    navigate('/sell-properties/seeAll');
    if (onItemClick) {
      onItemClick('My Properties');
    }
  };

  // Handle Inbox click
  const handleInboxClick = () => {
    resetAllActiveStates();
    setIsInboxActive(true);
    navigate('/sell-properties/inbox');
    if (onItemClick) {
      onItemClick('Inbox');
    }
  };

  // Handle Transactions click
  const handleTransactionsClick = () => {
    resetAllActiveStates();
    setIsTransactionsActive(true);
    navigate('/sell-properties/transactions');
    if (onItemClick) {
      onItemClick('Transactions');
    }
  };

  // Handle My Activity click
  const handleMyActivityClick = () => {
    resetAllActiveStates();
    setIsMyActivityActive(true);
    navigate('/profile');
    if (onItemClick) {
      onItemClick('My Activity');
    }
  };

  // Handle Wishlist click
  const handleWishlistClick = () => {
    resetAllActiveStates();
    setIsWishlistActive(true);
    navigate('/profile/wishlist');
    if (onItemClick) {
      onItemClick('Wishlist');
    }
  };

  // Handle Recent Searches click
  const handleRecentSearchesClick = () => {
    resetAllActiveStates();
    setIsRecentSearchesActive(true);
    navigate('/profile/recent-searches');
    if (onItemClick) {
      onItemClick('Recent Searches');
    }
  };

  return (
    <Box
      sx={{
        width: { xs: '100%', md: '300px' },
        backgroundColor: '#F4F2FF',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: '24px 10px 0',
        borderTopRightRadius: '25px',
        borderTopLeftRadius: '25px',
        mt: { xs: 0, md: '25px' },
        ml: { xs: 0, md: '80px' },
        boxSizing: 'border-box',
        minHeight: 'calc(100vh - 40px)',
      }}
    >
      <Box
        sx={{
          background: '#fff',
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          justifyContent: 'space-between',
          pt: '20px',
          pb: '12px',
          boxSizing: 'border-box',
        }}
      >
        <List sx={{ p: '0 16px', m: 0 }}>
          {/* Dashboard */}
          <ListItem
            button
            onClick={handleDashboardClick}
            sx={{
              py: 2,
              cursor: 'pointer',
              '&:hover': { bgcolor: 'rgba(244, 242, 255, 0.5)' },
              position: 'relative',
              bgcolor: isDashboardActive ? 'rgba(244, 242, 255, 0.3)' : 'transparent',
              '&::after': isDashboardActive ? {
                content: '""',
                position: 'absolute',
                bottom: '8px',
                left: '70px',
                width: '63px',
                height: '2px',
                backgroundColor: '#3366FF',
              } : {},
            }}
          >
            <ListItemIcon sx={{ minWidth: '40px' }}>
              <Box
                sx={{
                  bgcolor: isDashboardActive ? '#3366FF' : '#f5f5f5',
                  borderRadius: '8px',
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <DashboardIcon sx={{ color: isDashboardActive ? 'white' : '#1A2B4B', fontSize: 18 }} />
              </Box>
            </ListItemIcon>
            <ListItemText
              primary="Dashboard"
              sx={{
                '& .MuiTypography-root': {
                  fontWeight: isDashboardActive ? 600 : 500,
                  fontFamily: 'Outfit, sans-serif',
                  color: isDashboardActive ? '#3366FF' : 'inherit',
                }
              }}
            />
            <IconButton
              edge="end"
              sx={{
                backgroundColor: isDashboardActive ? '#3366FF' : '#f5f5f5',
                border: '1px solid rgba(229, 231, 235, 1)',
                borderRadius: '50%',
                width: 32,
                height: 32,
                '&:hover': {
                  backgroundColor: isDashboardActive ? '#2855e0' : '#e0e0e0',
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleDashboardClick();
              }}
            >
              <ChevronRightIcon sx={{ color: isDashboardActive ? 'white' : '#666', fontSize: 18 }} />
            </IconButton>
          </ListItem>

          {/* Edit Profile */}
          <ListItem
            button
            onClick={handleEditProfileClick}
            sx={{
              py: 2,
              cursor: 'pointer',
              '&:hover': { bgcolor: 'rgba(244, 242, 255, 0.5)' },
              position: 'relative',
              bgcolor: isEditProfileActive ? 'rgba(244, 242, 255, 0.3)' : 'transparent',
              '&::after': isEditProfileActive ? {
                content: '""',
                position: 'absolute',
                bottom: '8px',
                left: '70px',
                width: '77px',
                height: '2px',
                backgroundColor: '#3366FF',
              } : {},
            }}
          >
            <ListItemIcon sx={{ minWidth: '40px' }}>
              <Box
                sx={{
                  bgcolor: isEditProfileActive ? '#3366FF' : '#f5f5f5',
                  borderRadius: '8px',
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <EditIcon sx={{ color: isEditProfileActive ? 'white' : '#1A2B4B', fontSize: 18 }} />
              </Box>
            </ListItemIcon>
            <ListItemText
              primary="Edit Profile"
              sx={{
                '& .MuiTypography-root': {
                  fontWeight: isEditProfileActive ? 600 : 500,
                  fontFamily: 'Outfit, sans-serif',
                  color: isEditProfileActive ? '#3366FF' : 'inherit',
                }
              }}
            />
            <IconButton
              edge="end"
              sx={{
                backgroundColor: isEditProfileActive ? '#3366FF' : '#f5f5f5',
                border: '1px solid rgba(229, 231, 235, 1)',
                borderRadius: '50%',
                width: 32,
                height: 32,
                '&:hover': {
                  backgroundColor: isEditProfileActive ? '#2855e0' : '#e0e0e0',
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleEditProfileClick();
              }}
            >
              <ChevronRightIcon sx={{ color: isEditProfileActive ? 'white' : '#666', fontSize: 18 }} />
            </IconButton>
          </ListItem>

          {/* My Properties */}
          <ListItem
            button
            onClick={handleMyPropertiesClick}
            sx={{
              py: 2,
              cursor: 'pointer',
              '&:hover': { bgcolor: 'rgba(244, 242, 255, 0.5)' },
              position: 'relative',
              bgcolor: isMyPropertiesActive ? 'rgba(244, 242, 255, 0.3)' : 'transparent',
              '&::after': isMyPropertiesActive ? {
                content: '""',
                position: 'absolute',
                bottom: '8px',
                left: '70px',
                width: '91px',
                height: '2px',
                backgroundColor: '#3366FF',
              } : {},
            }}
          >
            <ListItemIcon sx={{ minWidth: '40px' }}>
              <Box
                sx={{
                  bgcolor: isMyPropertiesActive ? '#3366FF' : '#f5f5f5',
                  borderRadius: '8px',
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FavoriteIcon sx={{ color: isMyPropertiesActive ? 'white' : '#1A2B4B', fontSize: 18 }} />
              </Box>
            </ListItemIcon>
            <ListItemText
              primary="My Properties"
              sx={{
                '& .MuiTypography-root': {
                  fontWeight: isMyPropertiesActive ? 600 : 500,
                  fontFamily: 'Outfit, sans-serif',
                  color: isMyPropertiesActive ? '#3366FF' : 'inherit',
                }
              }}
            />
            <IconButton
              edge="end"
              sx={{
                backgroundColor: isMyPropertiesActive ? '#3366FF' : '#f5f5f5',
                border: '1px solid rgba(229, 231, 235, 1)',
                borderRadius: '50%',
                width: 32,
                height: 32,
                '&:hover': {
                  backgroundColor: isMyPropertiesActive ? '#2855e0' : '#e0e0e0',
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleMyPropertiesClick();
              }}
            >
              <ChevronRightIcon sx={{ color: isMyPropertiesActive ? 'white' : '#666', fontSize: 18 }} />
            </IconButton>
          </ListItem>

          {/* Inbox */}
          <ListItem
            button
            onClick={handleInboxClick}
            sx={{
              py: 2,
              cursor: 'pointer',
              '&:hover': { bgcolor: 'rgba(244, 242, 255, 0.5)' },
              position: 'relative',
              bgcolor: isInboxActive ? 'rgba(244, 242, 255, 0.3)' : 'transparent',
              '&::after': isInboxActive ? {
                content: '""',
                position: 'absolute',
                bottom: '8px',
                left: '70px',
                width: '35px',
                height: '2px',
                backgroundColor: '#3366FF',
              } : {},
            }}
          >
            <ListItemIcon sx={{ minWidth: '40px' }}>
              <Box
                sx={{
                  bgcolor: isInboxActive ? '#3366FF' : '#f5f5f5',
                  borderRadius: '8px',
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <MailIcon sx={{ color: isInboxActive ? 'white' : '#1A2B4B', fontSize: 18 }} />
              </Box>
            </ListItemIcon>
            <ListItemText
              primary="Inbox"
              sx={{
                '& .MuiTypography-root': {
                  fontWeight: isInboxActive ? 600 : 500,
                  fontFamily: 'Outfit, sans-serif',
                  color: isInboxActive ? '#3366FF' : 'inherit',
                }
              }}
            />
            <IconButton
              edge="end"
              sx={{
                backgroundColor: isInboxActive ? '#3366FF' : '#f5f5f5',
                border: '1px solid rgba(229, 231, 235, 1)',
                borderRadius: '50%',
                width: 32,
                height: 32,
                '&:hover': {
                  backgroundColor: isInboxActive ? '#2855e0' : '#e0e0e0',
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleInboxClick();
              }}
            >
              <ChevronRightIcon sx={{ color: isInboxActive ? 'white' : '#666', fontSize: 18 }} />
            </IconButton>
          </ListItem>

          {/* Transactions */}
          <ListItem
            button
            onClick={handleTransactionsClick}
            sx={{
              py: 2,
              cursor: 'pointer',
              '&:hover': { bgcolor: 'rgba(244, 242, 255, 0.5)' },
              position: 'relative',
              bgcolor: isTransactionsActive ? 'rgba(244, 242, 255, 0.3)' : 'transparent',
              '&::after': isTransactionsActive ? {
                content: '""',
                position: 'absolute',
                bottom: '8px',
                left: '70px',
                width: '84px',
                height: '2px',
                backgroundColor: '#3366FF',
              } : {},
            }}
          >
            <ListItemIcon sx={{ minWidth: '40px' }}>
              <Box
                sx={{
                  bgcolor: isTransactionsActive ? '#3366FF' : '#f5f5f5',
                  borderRadius: '8px',
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <SwapHorizIcon sx={{ color: isTransactionsActive ? 'white' : '#1A2B4B', fontSize: 18 }} />
              </Box>
            </ListItemIcon>
            <ListItemText
              primary="Transactions"
              sx={{
                '& .MuiTypography-root': {
                  fontWeight: isTransactionsActive ? 600 : 500,
                  fontFamily: 'Outfit, sans-serif',
                  color: isTransactionsActive ? '#3366FF' : 'inherit',
                }
              }}
            />
            <IconButton
              edge="end"
              sx={{
                backgroundColor: isTransactionsActive ? '#3366FF' : '#f5f5f5',
                border: '1px solid rgba(229, 231, 235, 1)',
                borderRadius: '50%',
                width: 32,
                height: 32,
                '&:hover': {
                  backgroundColor: isTransactionsActive ? '#2855e0' : '#e0e0e0',
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleTransactionsClick();
              }}
            >
              <ChevronRightIcon sx={{ color: isTransactionsActive ? 'white' : '#666', fontSize: 18 }} />
            </IconButton>
          </ListItem>

          <Divider sx={{ mx: 2 }} />

          {/* My Activity */}
          <ListItem
            button
            onClick={handleMyActivityClick}
            sx={{
              py: 2,
              cursor: 'pointer',
              '&:hover': { bgcolor: 'rgba(244, 242, 255, 0.5)' },
              position: 'relative',
              bgcolor: isMyActivityActive ? 'rgba(244, 242, 255, 0.3)' : 'transparent',
              '&::after': isMyActivityActive ? {
                content: '""',
                position: 'absolute',
                bottom: '8px',
                left: '70px',
                width: '77px',
                height: '2px',
                backgroundColor: '#3366FF',
              } : {},
            }}
          >
            <ListItemIcon sx={{ minWidth: '40px' }}>
              <Box
                sx={{
                  bgcolor: isMyActivityActive ? '#3366FF' : '#f5f5f5',
                  borderRadius: '8px',
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <PersonIcon sx={{ color: isMyActivityActive ? 'white' : '#1A2B4B', fontSize: 18 }} />
              </Box>
            </ListItemIcon>
            <ListItemText
              primary="My Activity"
              sx={{
                '& .MuiTypography-root': {
                  fontWeight: isMyActivityActive ? 600 : 500,
                  fontFamily: 'Outfit, sans-serif',
                  color: isMyActivityActive ? '#3366FF' : 'inherit',
                }
              }}
            />
            <IconButton
              edge="end"
              sx={{
                backgroundColor: isMyActivityActive ? '#3366FF' : '#f5f5f5',
                border: '1px solid rgba(229, 231, 235, 1)',
                borderRadius: '50%',
                width: 32,
                height: 32,
                '&:hover': {
                  backgroundColor: isMyActivityActive ? '#2855e0' : '#e0e0e0',
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleMyActivityClick();
              }}
            >
              <ChevronRightIcon sx={{ color: isMyActivityActive ? 'white' : '#666', fontSize: 18 }} />
            </IconButton>
          </ListItem>

          {/* Wishlist */}
          <ListItem
            button
            onClick={handleWishlistClick}
            sx={{
              py: 2,
              cursor: 'pointer',
              '&:hover': { bgcolor: 'rgba(244, 242, 255, 0.5)' },
              position: 'relative',
              bgcolor: isWishlistActive ? 'rgba(244, 242, 255, 0.3)' : 'transparent',
              '&::after': isWishlistActive ? {
                content: '""',
                position: 'absolute',
                bottom: '8px',
                left: '70px',
                width: '56px',
                height: '2px',
                backgroundColor: '#3366FF',
              } : {},
            }}
          >
            <ListItemIcon sx={{ minWidth: '40px' }}>
              <Box
                sx={{
                  bgcolor: isWishlistActive ? '#3366FF' : '#f5f5f5',
                  borderRadius: '8px',
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FavoriteIcon sx={{ color: isWishlistActive ? 'white' : '#1A2B4B', fontSize: 18 }} />
              </Box>
            </ListItemIcon>
            <ListItemText
              primary="Wishlist"
              sx={{
                '& .MuiTypography-root': {
                  fontWeight: isWishlistActive ? 600 : 500,
                  fontFamily: 'Outfit, sans-serif',
                  color: isWishlistActive ? '#3366FF' : 'inherit',
                }
              }}
            />
            <IconButton
              edge="end"
              sx={{
                backgroundColor: isWishlistActive ? '#3366FF' : '#f5f5f5',
                border: '1px solid rgba(229, 231, 235, 1)',
                borderRadius: '50%',
                width: 32,
                height: 32,
                '&:hover': {
                  backgroundColor: isWishlistActive ? '#2855e0' : '#e0e0e0',
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleWishlistClick();
              }}
            >
              <ChevronRightIcon sx={{ color: isWishlistActive ? 'white' : '#666', fontSize: 18 }} />
            </IconButton>
          </ListItem>

          {/* Recent Searches */}
          <ListItem
            button
            onClick={handleRecentSearchesClick}
            sx={{
              py: 2,
              cursor: 'pointer',
              '&:hover': { bgcolor: 'rgba(244, 242, 255, 0.5)' },
              position: 'relative',
              bgcolor: isRecentSearchesActive ? 'rgba(244, 242, 255, 0.3)' : 'transparent',
              '&::after': isRecentSearchesActive ? {
                content: '""',
                position: 'absolute',
                bottom: '8px',
                left: '70px',
                width: '112px',
                height: '2px',
                backgroundColor: '#3366FF',
              } : {},
            }}
          >
            <ListItemIcon sx={{ minWidth: '40px' }}>
              <Box
                sx={{
                  bgcolor: isRecentSearchesActive ? '#3366FF' : '#f5f5f5',
                  borderRadius: '8px',
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <SearchIcon sx={{ color: isRecentSearchesActive ? 'white' : '#1A2B4B', fontSize: 18 }} />
              </Box>
            </ListItemIcon>
            <ListItemText
              primary="Recent Searches"
              sx={{
                '& .MuiTypography-root': {
                  fontWeight: isRecentSearchesActive ? 600 : 500,
                  fontFamily: 'Outfit, sans-serif',
                  color: isRecentSearchesActive ? '#3366FF' : 'inherit',
                }
              }}
            />
            <IconButton
              edge="end"
              sx={{
                backgroundColor: isRecentSearchesActive ? '#3366FF' : '#f5f5f5',
                border: '1px solid rgba(229, 231, 235, 1)',
                borderRadius: '50%',
                width: 32,
                height: 32,
                '&:hover': {
                  backgroundColor: isRecentSearchesActive ? '#2855e0' : '#e0e0e0',
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleRecentSearchesClick();
              }}
            >
              <ChevronRightIcon sx={{ color: isRecentSearchesActive ? 'white' : '#666', fontSize: 18 }} />
            </IconButton>
          </ListItem>
        </List>
      </Box>
    </Box>
  );
};

export default Sidebar;